import { useAuth } from '@/contexts/AuthContext';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { FlaskConical, ShieldCheck, ArrowLeftRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

/**
 * Persistent badge showing whether the current session is Demo or Real Mode.
 * Renders nothing until auth is hydrated to avoid a flash of the wrong state.
 */
export function ModeBadge({ className }: { className?: string }) {
  const { mode, loading, dealershipName, role, loginDemo, logout } = useAuth();
  const navigate = useNavigate();
  if (loading || !mode) return null;

  const isDemo = mode === 'demo';
  const Icon = isDemo ? FlaskConical : ShieldCheck;
  const label = isDemo ? 'Demo' : 'Real';

  const handleToggle = async () => {
    if (isDemo) {
      // Switch to Real — if a supabase session already exists, it will rehydrate.
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        // Clear demo state; onAuthStateChange / getSession in AuthProvider will repopulate Real.
        await logout();
        // Trigger a rehydrate by refetching the session (AuthProvider listener handles it on next mount).
        // Navigating to dashboard will land on the real workspace once session loads.
        navigate('/dashboard');
        toast({ title: 'Switched to Real Mode', description: 'Loading your live workspace…' });
      } else {
        navigate('/login?tab=real');
        toast({ title: 'Sign in to use Real Mode', description: 'Enter your dealership credentials.' });
      }
    } else {
      // Switch to Demo — keep supabase session intact so user can switch back.
      loginDemo(role ?? 'manager');
      navigate(role === 'rep' ? '/rep' : '/dashboard');
      toast({ title: 'Switched to Demo Mode', description: 'Viewing sample data — nothing is saved.' });
    }
  };

  const nextLabel = isDemo ? 'Switch to Real Mode' : 'Switch to Demo Mode';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleToggle}
          aria-label={`${isDemo ? 'Demo Mode' : 'Real Mode'} — click to ${nextLabel}`}
          className={cn(
            'group inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            isDemo
              ? 'bg-amber-500/15 text-amber-300 border-amber-500/40 hover:bg-amber-500/25'
              : 'bg-primary/15 text-primary border-primary/40 hover:bg-primary/25',
            className,
          )}
        >
          <Icon className="h-3 w-3" />
          {label}
          <ArrowLeftRight className="h-3 w-3 opacity-60 group-hover:opacity-100" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-[260px] text-xs">
        <div className="font-semibold mb-1">
          {isDemo
            ? 'Sample data — nothing is saved.'
            : `Live data${dealershipName ? ` · ${dealershipName}` : ''}.`}
        </div>
        <div className="text-muted-foreground">Click to {nextLabel.toLowerCase()}.</div>
      </TooltipContent>
    </Tooltip>
  );
}